/**
 * Admin API Service
 * Handles admin-related API calls for AI section
 */

import { api } from './api-client';

/**
 * Model Configuration API endpoints
 * Core endpoints for AI section
 */
export const modelConfigurationsApi = {
  /**
   * Get overview data for AI section (jobs, providers, connections, configurations, active models)
   * @returns {Promise<Object>} Overview object with all AI section data
   */
  async getOverview() {
    return await api.get('/api/v1/admin/ai/model-configurations/overview/ai-section');
  },

  /**
   * Get detailed configuration info including job, connection, functions/tasks
   * @param {string} configId - Configuration UUID
   * @returns {Promise<Object>} Detailed configuration object
   */
  async getDetail(configId) {
    return await api.get(`/api/v1/admin/ai/model-configurations/${configId}/detail`);
  },

  /**
   * Create a new model configuration
   * @param {Object} data - Configuration data (name, model_id, job, connection_id, dimensions, provider_specific_params, main_system_prompt_template)
   * @returns {Promise<Object>} Created configuration object
   */
  async create(data) {
    return await api.post('/api/v1/admin/ai/model-configurations', data);
  },

  /**
   * Update model configuration settings
   * @param {string} configId - Configuration UUID
   * @param {Object} updates - Fields to update (name, dimensions, connection_id, job, provider_specific_params, main_system_prompt_template)
   * @returns {Promise<Object>} Updated configuration object
   */
  async update(configId, updates) {
    return await api.put(`/api/v1/admin/ai/model-configurations/${configId}`, updates);
  },

  /**
   * Delete a model configuration
   * @param {string} configId - Configuration UUID
   * @returns {Promise<Object>} Deletion confirmation
   */
  async delete(configId) {
    return await api.delete(`/api/v1/admin/ai/model-configurations/${configId}`);
  },

  /**
   * Create a new connection for a model configuration
   * @param {string} configId - Configuration UUID
   * @param {Object} data - Connection data (api_key)
   * @returns {Promise<Object>} Created connection object
   */
  async createConnection(configId, data) {
    return await api.post(`/api/v1/admin/ai/model-configurations/${configId}/create-connection`, data);
  },

  /**
   * Get prompt preview with parameters resolved
   * @param {string} configId - Configuration UUID
   * @returns {Promise<Object>} Preview object with system_prompt and function_prompts
   */
  async previewPrompts(configId) {
    return await api.get(`/api/v1/admin/ai/model-configurations/${configId}/preview-prompts`);
  },
};

/**
 * Function Configuration API endpoints
 */
export const functionConfigurationsApi = {
  /**
   * Update function configuration
   * @param {string} funcConfigId - Function configuration UUID
   * @param {Object} updates - Fields to update (prompt, response_template, parameters_schema, rate_limit, execution_timeout, description, category, enabled)
   * @returns {Promise<Object>} Updated function configuration
   */
  async update(funcConfigId, updates) {
    return await api.put(`/api/v1/admin/ai/functions/configurations/${funcConfigId}`, updates);
  },
};

/**
 * Task Configuration API endpoints
 */
export const taskConfigurationsApi = {
  /**
   * Update task configuration
   * @param {string} taskConfigId - Task configuration UUID
   * @param {Object} updates - Fields to update (prompt, enabled)
   * @returns {Promise<Object>} Updated task configuration
   */
  async update(taskConfigId, updates) {
    return await api.put(`/api/v1/admin/ai/tasks/configurations/${taskConfigId}`, updates);
  },
};

/**
 * Template Parameters API endpoints
 */
export const templateParametersApi = {
  /**
   * Update template parameter
   * @param {string} paramId - Parameter UUID
   * @param {Object} updates - Fields to update (name, description, type, value)
   * @returns {Promise<Object>} Updated parameter object
   */
  async update(paramId, updates) {
    return await api.put(`/api/v1/admin/ai/model-configurations/parameters/${paramId}`, updates);
  },
};

/**
 * User Management API endpoints
 */
export const usersApi = {
  /**
   * Get overview data for users section (company-scoped user list)
   * @returns {Promise<Object>} Overview object with users array
   */
  async getOverview() {
    return await api.get('/api/v1/admin/users/overview');
  },

  /**
   * Get detailed user info including role
   * @param {string} userId - User UUID
   * @returns {Promise<Object>} Detailed user object
   */
  async getDetail(userId) {
    return await api.get(`/api/v1/admin/users/${userId}/detail`);
  },

  /**
   * Update user information
   * @param {string} userId - User UUID
   * @param {Object} updates - Fields to update (full_name, email, is_active, role)
   * @returns {Promise<Object>} Updated user object
   */
  async update(userId, updates) {
    return await api.put(`/api/v1/admin/users/${userId}`, updates);
  },

  /**
   * Get user's product access details with reasons
   * @param {string} userId - User UUID
   * @returns {Promise<Object>} Object with products array containing access details
   */
  async getAccess(userId) {
    return await api.get(`/api/v1/admin/users/${userId}/access`);
  },

  /**
   * Get all available roles in the system
   * @returns {Promise<Object>} Object with roles array
   */
  async getAllRoles() {
    return await api.get('/api/v1/admin/users/roles');
  },

  /**
   * Get user's roles and all available roles
   * @param {string} userId - User UUID
   * @returns {Promise<Object>} Object with user_roles and available_roles arrays
   */
  async getRoles(userId) {
    return await api.get(`/api/v1/admin/users/${userId}/roles`);
  },

  /**
   * Add a role to a user
   * @param {string} userId - User UUID
   * @param {string} roleId - Role UUID to add
   * @returns {Promise<Object>} Success message
   */
  async addRole(userId, roleId) {
    return await api.post(`/api/v1/admin/users/${userId}/roles`, { role_id: roleId });
  },

  /**
   * Remove a role from a user
   * @param {string} userId - User UUID
   * @param {string} roleId - Role UUID to remove
   * @returns {Promise<Object>} Success message
   */
  async removeRole(userId, roleId) {
    return await api.delete(`/api/v1/admin/users/${userId}/roles/${roleId}`);
  },

  /**
   * Create a new role
   * @param {string} roleName - Name of the role
   * @param {string} theme - Color theme for the role (blue, green, purple, amber)
   * @returns {Promise<Object>} Created role object
   */
  async createRole(roleName, theme = 'blue') {
    return await api.post('/api/v1/admin/users/roles', { name: roleName, theme: theme });
  },

  /**
   * Get product access for a specific role
   * @param {string} roleId - Role UUID
   * @returns {Promise<Object>} Object with products array containing access details
   */
  async getRoleAccess(roleId) {
    return await api.get(`/api/v1/admin/users/roles/${roleId}/access`);
  },

  /**
   * Get user's activity history
   * @param {string} userId - User UUID
   * @param {number} limit - Maximum number of activities to return (default 100)
   * @param {number} offset - Number of activities to skip for pagination (default 0)
   * @returns {Promise<Object>} Object with activities array and total_count
   */
  async getActivities(userId, limit = 100, offset = 0) {
    return await api.get(`/api/v1/admin/users/${userId}/activities?limit=${limit}&offset=${offset}`);
  },

  /**
   * Get users who have a specific role
   * @param {string} roleId - Role UUID
   * @returns {Promise<Object>} Object with users array
   */
  async getRoleUsers(roleId) {
    return await api.get(`/api/v1/admin/users/roles/${roleId}/users`);
  },

  /**
   * Delete a user
   * @param {string} userId - User UUID
   * @returns {Promise<Object>} Deletion confirmation
   */
  async delete(userId) {
    return await api.delete(`/api/v1/admin/users/${userId}`);
  },
};

/**
 * Product Management API endpoints
 */
export const productsApi = {
  /**
   * Get overview data for products section (company-scoped product list)
   * @returns {Promise<Object>} Overview object with products array
   */
  async getOverview() {
    return await api.get('/api/v1/admin/products/overview');
  },

  /**
   * Create a new product with optional role and user assignments
   * @param {Object} productData - Product data including name, version, description, role_assignments, user_assignments
   * @returns {Promise<Object>} Created product object
   */
  async create(productData) {
    return await api.post('/api/v1/admin/products/', productData);
  },

  /**
   * Get detailed product info including statistics
   * @param {string} productId - Product UUID
   * @returns {Promise<Object>} Detailed product object
   */
  async getDetail(productId) {
    return await api.get(`/api/v1/admin/products/${productId}/detail`);
  },

  /**
   * Update product information
   * @param {string} productId - Product UUID
   * @param {Object} updates - Fields to update (name, version, description)
   * @returns {Promise<Object>} Updated product object
   */
  async update(productId, updates) {
    return await api.put(`/api/v1/admin/products/${productId}`, updates);
  },

  /**
   * Get product's user access details with reasons
   * @param {string} productId - Product UUID
   * @returns {Promise<Object>} Object with users array containing access details
   */
  async getAccess(productId) {
    return await api.get(`/api/v1/admin/products/${productId}/access`);
  },

  /**
   * Get product's role access details
   * @param {string} productId - Product UUID
   * @returns {Promise<Object>} Object with product_roles and available_roles arrays
   */
  async getRoleAccess(productId) {
    return await api.get(`/api/v1/admin/products/${productId}/role-access`);
  },

  /**
   * Grant a role access to a product
   * @param {string} productId - Product UUID
   * @param {string} roleId - Role UUID to grant access
   * @param {string} accessLevel - Access level (read, write, admin)
   * @returns {Promise<Object>} Success message
   */
  async addRole(productId, roleId, accessLevel = 'read') {
    return await api.post(`/api/v1/admin/products/${productId}/roles`, { role_id: roleId, access_level: accessLevel });
  },

  /**
   * Remove role access from a product
   * @param {string} productId - Product UUID
   * @param {string} roleId - Role UUID to remove access
   * @returns {Promise<Object>} Success message
   */
  async removeRole(productId, roleId) {
    return await api.delete(`/api/v1/admin/products/${productId}/roles/${roleId}`);
  },

  /**
   * Add direct user access to a product
   * @param {string} productId - Product UUID
   * @param {string} userId - User UUID
   * @param {string} accessLevel - Access level (read, write, admin)
   * @returns {Promise<Object>} Success message
   */
  async addUser(productId, userId, accessLevel = 'read') {
    return await api.post(`/api/v1/admin/products/${productId}/users`, { user_id: userId, access_level: accessLevel });
  },

  /**
   * Remove direct user access from a product
   * @param {string} productId - Product UUID
   * @param {string} userId - User UUID to remove access
   * @returns {Promise<Object>} Success message
   */
  async removeUser(productId, userId) {
    return await api.delete(`/api/v1/admin/products/${productId}/users/${userId}`);
  },

  /**
   * Get product's activity history
   * @param {string} productId - Product UUID
   * @param {number} limit - Maximum number of activities to return (default 100)
   * @param {number} offset - Number of activities to skip for pagination (default 0)
   * @returns {Promise<Object>} Object with activities array and total_count
   */
  async getActivities(productId, limit = 100, offset = 0) {
    return await api.get(`/api/v1/admin/products/${productId}/activities?limit=${limit}&offset=${offset}`);
  },

  /**
   * Delete a product
   * @param {string} productId - Product UUID
   * @returns {Promise<Object>} Deletion confirmation
   */
  async delete(productId) {
    return await api.delete(`/api/v1/admin/products/${productId}`);
  },
};

/**
 * Restore Operations API endpoints
 */
export const restoreApi = {
  /**
   * Reset all AI configurations to initial state from prompt files
   * WARNING: This deletes all existing configurations and recreates from prompt files
   * @returns {Promise<Object>} Reset confirmation with counts
   */
  async resetToInitial() {
    return await api.post('/api/v1/admin/restore/reset-to-initial');
  },

  /**
   * Restore configurations from defaults table
   * Copies from ai_default_* tables to active ai_model_configurations
   * @returns {Promise<Object>} Restore confirmation
   */
  async restoreFromDefaults() {
    return await api.post('/api/v1/admin/restore/restore-from-defaults');
  },

  /**
   * Set current active configurations as new defaults
   * Copies from active ai_model_configurations to ai_default_* tables
   * @returns {Promise<Object>} Save confirmation
   */
  async setCurrentAsDefault() {
    return await api.post('/api/v1/admin/restore/set-current-as-default');
  },
};

/**
 * Invitations API endpoints
 */
export const invitationsApi = {
  /**
   * Create a new user invitation
   * @param {Object} data - Invitation data (role_ids, product_access, email, notes, expires_in_days)
   * @returns {Promise<Object>} Created invitation with token
   */
  async create(data) {
    return await api.post('/api/v1/auth/invitations', data);
  },

  /**
   * Get all invitations for the company
   * @param {boolean} includeUsed - Include already-used invitations
   * @param {boolean} includeExpired - Include expired invitations
   * @returns {Promise<Array>} List of invitations
   */
  async getAll(includeUsed = false, includeExpired = false) {
    return await api.get(`/api/v1/auth/invitations?include_used=${includeUsed}&include_expired=${includeExpired}`);
  },

  /**
   * Validate an invitation token
   * @param {string} token - Invitation token UUID
   * @returns {Promise<Object>} Validation result with invitation details
   */
  async validate(token) {
    return await api.get(`/api/v1/auth/invitations/validate/${token}`);
  },

  /**
   * Revoke an invitation
   * @param {string} invitationId - Invitation UUID to revoke
   * @returns {Promise<void>}
   */
  async revoke(invitationId) {
    return await api.delete(`/api/v1/auth/invitations/${invitationId}`);
  },
};

/**
 * Model Connections API endpoints
 */
export const modelConnectionsApi = {
  /**
   * Update model connection settings
   * @param {string} connectionId - Connection UUID
   * @param {Object} updates - Fields to update (name, provider_id, api_key)
   * @returns {Promise<Object>} Updated connection object
   */
  async update(connectionId, updates) {
    return await api.put(`/api/v1/admin/ai/model-connections/${connectionId}`, updates);
  },

  /**
   * Delete a model connection
   * @param {string} connectionId - Connection UUID
   * @returns {Promise<Object>} Deletion confirmation
   */
  async delete(connectionId) {
    return await api.delete(`/api/v1/admin/ai/model-connections/${connectionId}`);
  },
};

/**
 * AI Models API endpoints
 */
export const aiModelsApi = {
  /**
   * Get all AI models
   * @param {boolean} activeOnly - Filter to only active models (default: true)
   * @returns {Promise<Array>} List of AI models
   */
  async getAll(activeOnly = true) {
    return await api.get(`/api/v1/admin/ai/models?active_only=${activeOnly}`);
  },

  /**
   * Update an AI model
   * @param {string} modelId - Model UUID
   * @param {Object} updates - Fields to update (name, description, model_identifier, type, provider, is_active)
   * @returns {Promise<Object>} Updated model object
   */
  async update(modelId, updates) {
    return await api.put(`/api/v1/admin/ai/models/${modelId}`, updates);
  },

  /**
   * Delete an AI model
   * @param {string} modelId - Model UUID
   * @returns {Promise<Object>} Deletion confirmation
   */
  async delete(modelId) {
    return await api.delete(`/api/v1/admin/ai/models/${modelId}`);
  },
};

/**
 * Prompt Parameters API endpoints (Custom company-specific parameters)
 */
export const promptParametersApi = {
  /**
   * Get all parameters (SYSTEM + custom) with available template variables
   * @returns {Promise<Object>} Object with system_parameters, custom_parameters, and available_variables arrays
   */
  async getAll() {
    return await api.get('/api/v1/admin/ai/prompt-parameters');
  },

  /**
   * Create a new custom parameter
   * @param {Object} data - Parameter data (name, description, content, is_template)
   * @returns {Promise<Object>} Created parameter object
   */
  async create(data) {
    return await api.post('/api/v1/admin/ai/prompt-parameters', data);
  },

  /**
   * Update a custom parameter
   * @param {string} paramId - Parameter UUID
   * @param {Object} updates - Fields to update (description, content, is_template)
   * @returns {Promise<Object>} Updated parameter object
   */
  async update(paramId, updates) {
    return await api.put(`/api/v1/admin/ai/prompt-parameters/${paramId}`, updates);
  },

  /**
   * Delete a custom parameter
   * @param {string} paramId - Parameter UUID
   * @returns {Promise<Object>} Deletion confirmation
   */
  async delete(paramId) {
    return await api.delete(`/api/v1/admin/ai/prompt-parameters/${paramId}`);
  },
};

/**
 * Roles API endpoints
 */
export const rolesApi = {
  /**
   * Get overview data for roles section (list of all roles)
   * @returns {Promise<Object>} Overview object with roles array
   */
  async getOverview() {
    return await api.get('/api/v1/admin/roles/overview');
  },

  /**
   * Get detailed role info showing users and permissions
   * @param {string} roleId - Role ID (UUID)
   * @returns {Promise<Object>} Detailed role object with users and permissions
   */
  async getDetail(roleId) {
    return await api.get(`/api/v1/admin/roles/${roleId}/detail`);
  },

  /**
   * Create a new role
   * @param {Object} data - Role data (name, theme)
   * @returns {Promise<Object>} Created role object
   */
  async create(data) {
    return await api.post('/api/v1/admin/roles/', data);
  },

  /**
   * Delete a role
   * @param {string} roleId - Role UUID
   * @returns {Promise<Object>} Deletion confirmation
   */
  async delete(roleId) {
    return await api.delete(`/api/v1/admin/roles/${roleId}`);
  },
};

/**
 * Permissions API endpoints
 */
export const permissionsApi = {
  /**
   * Get overview data for permissions section (list of all permissions)
   * @returns {Promise<Object>} Overview object with permissions array
   */
  async getOverview() {
    return await api.get('/api/v1/admin/permissions/overview');
  },

  /**
   * Get detailed permission info showing which users and roles have this permission
   * @param {string} permissionId - Permission ID (e.g., "create_invites")
   * @returns {Promise<Object>} Detailed permission object with users and roles
   */
  async getDetail(permissionId) {
    return await api.get(`/api/v1/admin/permissions/${permissionId}/detail`);
  },
};

/**
 * Combined admin API export
 */
export const adminApi = {
  modelConfigurations: modelConfigurationsApi,
  modelConnections: modelConnectionsApi,
  aiModels: aiModelsApi,
  functionConfigurations: functionConfigurationsApi,
  taskConfigurations: taskConfigurationsApi,
  templateParameters: templateParametersApi,
  promptParameters: promptParametersApi,
  users: usersApi,
  products: productsApi,
  roles: rolesApi,
  permissions: permissionsApi,
  restore: restoreApi,
  invitations: invitationsApi,
};

export default adminApi;
